<script lang="ts">
	import RoundArrowForward from 'virtual:icons/ic/round-arrow-forward';
	import RoundKeyboardArrowDown from 'virtual:icons/ic/round-keyboard-arrow-down';
	import RoundClose from 'virtual:icons/ic/round-close';
	import Container from './../Container.svelte';
	import { fly } from 'svelte/transition';
	import { cubicInOut } from 'svelte/easing';
	import { PUBLIC_API_URL } from '$env/static/public';
	import { isLockScroll } from '@lib/stores/main-layout.store';
	import { onDestroy, createEventDispatcher, type ComponentType } from 'svelte';
	import { page } from '$app/stores';
	import Avatar from './Avatar.svelte';
	import LogoutButton from './LogoutButton.svelte';
	import Modal from '../Modal.svelte';
	import ChangePasswordPanel from '@lib/components/auth/ChangePasswordPanel.svelte';
	import { bookMenuItems, getMenuItems } from '@lib/data/main-menu-items';
	import { writable, type Writable } from 'svelte/store';
	import Spinner from '../micro/Spinner.svelte';
	import { getBundles, getCategories, searchBooks } from '@lib/utils';
	import { toast } from 'svelte-sonner';
	import type { BookData, BundleData, CategoryItem } from '@lib/types';
	import { slugify } from '@lib/helpers/global';
	import BookCard from '../BookCard.svelte';
	import BundleCard from '@lib/components/app/home/BundleCard.svelte';

	const dispatch = createEventDispatcher();
	const { isLoggedIn, profile } = $page.data;

	type Identifier = (typeof bookMenuItems)[number]['identifier'];

	interface MenuItem {
		label: string;
		path?: string;
		icon?: ComponentType;
		isActive: boolean;
		identifier?: Identifier;
	}

	const modifiedBookMenuItems: Writable<MenuItem[]> = writable(
		bookMenuItems.map((item) => ({
			...item,
			isActive: true
		}))
	);

	const mainMenuItems = getMenuItems({
		isOnHome: $page.url.pathname === '/',
		isLoggedIn
	});

	const menuItems: Writable<MenuItem[][]> = writable([$modifiedBookMenuItems, ...mainMenuItems]);

	$isLockScroll = true;
	onDestroy(() => ($isLockScroll = false));

	let modalEl: HTMLDialogElement;

	const activeIdentifier: Writable<Identifier | undefined> = writable(undefined);
	const isLoadingBookData = writable(false);
	const activeIdentifierId = writable<number>();
	const categories: Writable<CategoryItem[]> = writable([]);
	const allBooks: Writable<BookData[]> = writable([]);
	const freeBooks: Writable<BookData[]> = writable([]);
	const comingSoonBooks: Writable<BookData[]> = writable([]);
	const bundles: Writable<BundleData[]> = writable([]);

	const BOOK_GRID_LIMIT = 5;
	const BUNDLE_GRID_LIMIT = 5;
	const CATEGORIES_LIMIT = 51;

	async function handleLoadBookMenu(item: MenuItem) {
		if (item.identifier === $activeIdentifier) {
			$activeIdentifier = undefined;
			return;
		}

		$isLoadingBookData = true;

		switch (item.identifier) {
			case 'category':
				$categories = await getCategories({ limit: String(CATEGORIES_LIMIT) });
				break;
			case 'all-books':
				$allBooks = await getBooks({ limit: String(BOOK_GRID_LIMIT) });
				break;
			case 'free-books':
				$freeBooks = await getBooks({ is_free: String(true), limit: String(BOOK_GRID_LIMIT) });
				break;
			case 'coming-soon':
				$comingSoonBooks = await getBooks({
					is_published: String(false),
					limit: String(BOOK_GRID_LIMIT)
				});
				break;
			case 'bundles':
				$bundles = await getBundles({ limit: String(BUNDLE_GRID_LIMIT) }).then((response) => {
					if (!response) return [];

					const { data } = response;
					return data;
				});
				break;
		}

		$isLoadingBookData = false;
		$activeIdentifier = item.identifier;
	}

	async function getBooks(params?: Record<string, string>) {
		const apiUrl = new URL(`${PUBLIC_API_URL}/book-digital`);
		const books = await searchBooks(apiUrl, {
			alternateParams: {
				limit: String(BOOK_GRID_LIMIT),
				...params
			}
		})
			.then((response) => {
				if (!response) return [];

				const { data } = response;
				return data;
			})
			.catch(() => {
				toast.error(`Oops, terjadi kesalahan saat memuat buku`);
				return [];
			});

		return books;
	}
</script>

<div
	transition:fly={{ duration: 500, y: 200, easing: cubicInOut }}
	class="menu-panel fixed inset-0 size-full overflow-y-auto bg-base-100"
>
	<div class="sticky top-0 z-[1] border-b bg-base-100 py-4">
		<Container>
			<div class="flex items-center gap-3">
				<button on:click={() => dispatch('close')} type="button" aria-label="Tutup" title="Tutup">
					<RoundClose class="text-2xl" />
				</button>
				<p class="text-lg font-semibold">Menu Utama</p>
			</div>
		</Container>
	</div>
	<Container class="py-4">
		{#if isLoggedIn && profile}
			<div class="custom-avatar border-b pb-4">
				<Avatar isClickable={false} pictureClass="size-14" class="gap-4">
					<div slot="details">
						<p class="font-medium">{profile.name}</p>
						<p class="mt-1 text-xs text-gray-500">{profile.email}</p>
					</div>
				</Avatar>
				<button
					on:click={() => modalEl.showModal()}
					type="button"
					class="btn btn-outline btn-block mt-4 border-gray-300 text-gray-500"
				>
					Ganti Password
				</button>
			</div>
		{:else}
			<div class="mb-2 grid grid-cols-2 gap-3">
				<a href="/login" class="btn btn-primary"> Masuk </a>
				<a href="/register" class="btn btn-outline btn-primary"> Daftar </a>
			</div>
		{/if}
		{#each $menuItems as items}
			<ul class="border-b py-2.5 last:border-none">
				{#each items.filter((item) => item.isActive) as item, index}
					<li>
						{#if item.identifier}
							{@const isOpenPanel = $activeIdentifier === item.identifier}
							<button
								on:click={() => handleLoadBookMenu(item)}
								on:click={() => ($activeIdentifierId = index)}
								type="button"
								class="btn-block flex items-center justify-between gap-4 py-4"
							>
								{item.label}
								{#if $isLoadingBookData && $activeIdentifierId === index}
									<Spinner />
								{:else}
									<RoundKeyboardArrowDown
										class="size-5 {isOpenPanel ? 'rotate-180' : ''} duration-300"
									/>
								{/if}
							</button>
							{#if isOpenPanel}
								<div class="border-t py-4">
									{#if item.identifier === 'category'}
										{#if $categories.length}
											<div class="grid grid-cols-2 gap-3 pre-lg:grid-cols-3">
												{#each $categories.slice(0, CATEGORIES_LIMIT - 1) as category}
													<a
														href="/categories/{category.slug || slugify(category.name)}"
														class="max-w-fit text-sm font-medium hover:text-secondary"
														>{category.name}</a
													>
												{/each}
											</div>
											{#if $categories.length > CATEGORIES_LIMIT - 1}
												<a
													href="/search"
													class="mt-5 flex items-center gap-1 font-medium text-blue-500"
												>
													Lihat Semua
													<RoundArrowForward class="text-lg" />
												</a>
											{/if}
										{:else}
											<p>Tidak ada kategori...</p>
										{/if}
									{/if}
									{#if item.identifier === 'all-books'}
										{#if $allBooks.length}
											<div class="books-grid">
												{#each $allBooks.slice(0, BOOK_GRID_LIMIT - 1) as book}
													<BookCard {book} />
												{/each}
											</div>
											{#if $allBooks.length > BOOK_GRID_LIMIT - 1}
												<a
													href="/search"
													class="mt-5 flex items-center gap-1 font-medium text-blue-500"
												>
													Lihat Semua
													<RoundArrowForward class="text-lg" />
												</a>
											{/if}
										{:else}
											<p>Tidak ada buku...</p>
										{/if}
									{/if}
									{#if item.identifier === 'free-books'}
										{#if $freeBooks.length}
											<div class="books-grid">
												{#each $freeBooks.slice(0, BOOK_GRID_LIMIT - 1) as book}
													<BookCard {book} />
												{/each}
											</div>
											{#if $freeBooks.length > BOOK_GRID_LIMIT - 1}
												<a
													href="/search"
													class="mt-5 flex items-center gap-1 font-medium text-blue-500"
												>
													Lihat Semua
													<RoundArrowForward class="text-lg" />
												</a>
											{/if}
										{:else}
											<p>Tidak ada buku gratis...</p>
										{/if}
									{/if}
									{#if item.identifier === 'coming-soon'}
										{#if $comingSoonBooks.length}
											<div class="books-grid">
												{#each $comingSoonBooks.slice(0, BOOK_GRID_LIMIT - 1) as book}
													<BookCard {book} />
												{/each}
											</div>
											{#if $comingSoonBooks.length > BOOK_GRID_LIMIT - 1}
												<a
													href="/search"
													class="mt-5 flex items-center gap-1 font-medium text-blue-500"
												>
													Lihat Semua
													<RoundArrowForward class="text-lg" />
												</a>
											{/if}
										{:else}
											<p>Tidak ada buku yang akan terbit...</p>
										{/if}
									{/if}
									{#if item.identifier === 'bundles'}
										{#if $bundles.length}
											<div class="bundles-grid">
												{#each $bundles as bundle}
													<BundleCard {bundle} />
												{/each}
											</div>
											{#if $bundles.length > BUNDLE_GRID_LIMIT - 1}
												<a
													href="/bundles"
													class="mt-5 flex items-center gap-1 font-medium text-blue-500"
												>
													Lihat Semua
													<RoundArrowForward class="text-lg" />
												</a>
											{/if}
										{:else}
											<p>Tidak ada bundle premium...</p>
										{/if}
									{/if}
								</div>
							{/if}
						{:else}
							<a class="flex items-center gap-4 py-4" href={item.path}>
								<svelte:component this={item.icon} class="text-2xl" />
								{item.label}
							</a>
						{/if}
					</li>
				{/each}
			</ul>
		{/each}
		{#if isLoggedIn}
			<LogoutButton />
		{/if}
	</Container>
</div>

<Modal bind:modalEl>
	<ChangePasswordPanel bind:modalEl />
</Modal>

<style lang="postcss">
	.books-grid {
		@apply grid grid-cols-2 gap-3 pre-lg:grid-cols-4;
	}

	.bundles-grid {
		@apply grid gap-3;
	}
</style>
